import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  User, Settings, Key,
  Bell, Shield, History, Save, RotateCcw, Upload, Camera,
  Edit3, X, AlertCircle
} from 'lucide-react';
import useChangePasswordModal from '../../hooks/useChangePasswordModal';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '../../components/ui';
import { uploadFile, getActiveProfileImageUrl } from '../../api/fileService';
import { apiGet, apiDelete } from '../../services/apiClient';

const UnifiedProfile = () => {
  const { openChangePasswordModal, changePasswordModal } = useChangePasswordModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'personal';
  
  // State from UserProfileManagement
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const fileInputRef = React.useRef(null);
  const PROFILE_PHOTO_FILETYPE_ID = Number(process.env.REACT_APP_PROFILE_PHOTO_FILETYPE_ID || 1);
  const [isAvatarPreviewOpen, setIsAvatarPreviewOpen] = useState(false);
  
  // Form state for Personal Info
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    dateOfBirth: '',
    bio: ''
  });

  // State from Profile (for notifications list)
  const [notifications] = useState([
    {
      id: 1,
      type: 'ticket_update',
      message: 'Agent Smith updated your ticket TKT-001',
      timestamp: '2025-01-15T10:30:00Z',
      read: false
    },
    {
      id: 2,
      type: 'ticket_resolved',
      message: 'Your ticket TKT-003 has been resolved',
      timestamp: '2025-01-15T09:15:00Z',
      read: true
    },
    {
      id: 3,
      type: 'system',
      message: 'System maintenance scheduled for tonight',
      timestamp: '2025-01-15T08:00:00Z',
      read: false
    }
  ]);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        const userId = sessionStorage.getItem('userId');
        if (!userId) {
          setError('User ID not found. Please login again.');
          return;
        }
        
        const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);
        //console.log('User profile data:', response);
        
        if (response?.data) {
          // Map API response to expected format
          const userData = response.data;
          const mappedProfileData = {
            userProfile: {
              personalInfo: {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                bio: userData.bio || '',
                dateOfBirth: userData.dateOfBirth || ''
              },
              contactInfo: {
                email: userData.email || '',
                phone: userData.phone || '',
                alternatePhone: userData.alternatePhone || '',
                address: {
                  city: userData.city || ''
                },
                socialMedia: {
                  linkedin: userData.linkedin || ''
                }
              },
              workInfo: {
                employeeId: userData.employeeId || userData.userId?.toString() || '',
                department: userData.departmentName || userData.department || '',
                jobTitle: userData.jobTitle || '',
                manager: userData.manager || '',
                startDate: userData.startDate || '',
                workLocation: userData.workLocation || ''
              },
              skills: userData.skills || [],
              certifications: userData.certifications || [],
              availability: {
                status: userData.status || 'online',
                customMessage: userData.customMessage || '',
                timezone: userData.timezone || 'UTC'
              },
              preferences: {
                language: userData.language || 'English',
                dateFormat: userData.dateFormat || 'MM/DD/YYYY',
                timeFormat: userData.timeFormat || '12h',
                theme: userData.theme || 'light'
              },
              privacy: {
                profileVisibility: userData.profileVisibility || 'team',
                contactInfoVisibility: userData.contactInfoVisibility || 'team',
                showOnlineStatus: userData.showOnlineStatus !== undefined ? userData.showOnlineStatus : true
              },
              lastUpdated: userData.updatedAt || userData.createdAt || new Date().toISOString()
            },
            profileStats: {
              profileCompleteness: userData.profileCompleteness || 0,
              totalSkills: userData.skills?.length || 0,
              activeCertifications: userData.certifications?.filter(c => c.status === 'active')?.length || 0
            }
          };
          
          setProfileData(mappedProfileData);
          
          // Load active profile image for avatar
          try {
            const imgUrl = await getActiveProfileImageUrl(userId);
            if (imgUrl) {
              setProfileData(prev => {
                if (!prev?.userProfile) return prev;
                return {
                  ...prev,
                  userProfile: {
                    ...prev.userProfile,
                    personalInfo: {
                      ...prev.userProfile.personalInfo,
                      avatar: imgUrl
                    }
                  }
                };
              });
            }
          } catch (e) {
            console.warn('Failed to load profile image:', e);
          }
          // Initialize form data from profile
          if (mappedProfileData?.userProfile) {
            const profile = mappedProfileData.userProfile;
            setFormData({
              firstName: profile.personalInfo?.firstName || '',
              lastName: profile.personalInfo?.lastName || '',
              email: profile.contactInfo?.email || '',
              phone: profile.contactInfo?.phone || '',
              city: profile.contactInfo?.address?.city || '',
              dateOfBirth: profile.personalInfo?.dateOfBirth || '',
              bio: profile.personalInfo?.bio || ''
            });
          }
        } else {
          setError('Failed to load profile data');
        }
      } catch (err) {
        setError('An error occurred while fetching profile data.');
        console.error('Error fetching profile data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, setSearchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleSave = () => {
    if (!profileData?.userProfile) return;
    
    // Update profile data with form data
    const updatedProfile = {
      ...profileData.userProfile,
      personalInfo: {
        ...profileData.userProfile.personalInfo,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        bio: formData.bio
      },
      contactInfo: {
        ...profileData.userProfile.contactInfo,
        email: formData.email,
        phone: formData.phone,
        address: {
          ...profileData.userProfile.contactInfo?.address,
          city: formData.city
        }
      }
    };
    
    // Update profile data
    setProfileData({
      ...profileData,
      userProfile: updatedProfile
    });
    
    // Save to localStorage or API (mock for now)
    try {
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
    } catch (err) {
      console.error('Failed to save profile:', err);
    }
    
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleReset = () => {
    if (!profileData?.userProfile) return;
    
    // Reset form data to original profile values
    const profile = profileData.userProfile;
    setFormData({
      firstName: profile.personalInfo?.firstName || '',
      lastName: profile.personalInfo?.lastName || '',
      email: profile.contactInfo?.email || '',
      phone: profile.contactInfo?.phone || '',
      city: profile.contactInfo?.address?.city || '',
      dateOfBirth: profile.personalInfo?.dateOfBirth || '',
      bio: profile.personalInfo?.bio || ''
    });
    
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleAvatarUploadClick = () => {
    // Ensure editing state so related UI updates can occur
    //console.log('handleAvatarUploadClick');
    if (!isEditing) {
      setIsEditing(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    try {
      // Frontend size guard (5 MB) with actual size info
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
      if (file.size > 5 * 1024 * 1024) {
        setAvatarError(`File size ${sizeMb} MB exceeds the maximum allowed 5 MB.`);
        return;
      }
      setUploadingAvatar(true);
      const userId = Number(sessionStorage.getItem('userId'));
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Upload to file service using screenName PROFILE
      const resp = await uploadFile(file, {
        reg_id: userId,
        // Do not send fileTypeId; backend will enforce 'Profile' type
        screenName: 'PROFILE',
        isActive: true
      });

      // Immediate preview with object URL
      const previewUrl = URL.createObjectURL(file);

      // Persist minimal metadata from API response if needed
      const documentPath = resp?.data?.data?.documentPath || resp?.data?.documentPath;

      // Update local state so the avatar renders
      setProfileData(prev => {
        if (!prev?.userProfile) return prev;
        const updated = {
          ...prev,
          userProfile: {
            ...prev.userProfile,
            personalInfo: {
              ...prev.userProfile.personalInfo,
              avatar: previewUrl,
              avatarDocumentPath: documentPath
            }
          }
        };
        return updated;
      });

      setHasChanges(true);
    } catch (err) {
      console.error('Avatar upload failed:', err);
      const msg = err?.message || 'Failed to upload profile photo. Please try again.';
      setAvatarError(msg);
    } finally {
      setUploadingAvatar(false);
      // Reset the input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const tabs = useMemo(() => [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: Settings },
    { id: 'activity', label: 'Activity', icon: History }
  ], []);

  // Sync active tab with URL query changes (e.g., when navigating from header dropdown)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      const isValidTab = tabs.some(tab => tab.id === tabParam);
      if (isValidTab) {
        setActiveTab(tabParam);
        setIsEditing(false);
        setHasChanges(false);
      }
    }
  }, [searchParams, activeTab, tabs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No profile data available</p>
        </div>
      </div>
    );
  }

  const { userProfile, availableLanguages, dateFormats, timeZones } = profileData;

  // Merge preferences from Profile (read-only) and UserProfileManagement (editable)
  const mergedPreferences = {
    ...userProfile.preferences,
    // Add any additional preferences from Profile if needed
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full py-8">
        <div className="px-4 sm:px-6">
          <div className="flex flex-col gap-4 mb-8 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">My Profile</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage your profile information and preferences1
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={openChangePasswordModal}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-[#0B2530] bg-white border border-gray-200 rounded-md hover:bg-[#F8FCFB] transition-colors"
              >
                <Key className="h-4 w-4" />
                <span>Change Password</span>
              </button>
              <button
                onClick={handleReset}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8">
            {/* Main Content - Full Width */}
            <div>
              <Card>
                {/* Personal Info Tab (from UserProfileManagement) - Always visible */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Personal Information</h2>
                    <button
                      onClick={() => {
                        if (isEditing) {
                          // Cancel editing - reset form
                          handleReset();
                        } else {
                          setIsEditing(true);
                        }
                      }}
                      className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>{isEditing ? 'Cancel' : 'Edit'}</span>
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Profile Picture */}
                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        <div className="h-24 w-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          {userProfile.personalInfo?.avatar ? (
                            <img
                              src={userProfile.personalInfo.avatar}
                              alt="Profile"
                              className="h-24 w-24 rounded-full cursor-pointer object-cover"
                              onClick={() => setIsAvatarPreviewOpen(true)}
                            />
                          ) : (
                            <span className="text-2xl font-bold text-gray-400">
                              {userProfile.personalInfo?.firstName?.[0]}{userProfile.personalInfo?.lastName?.[0]}<br />
                              {userProfile.personalInfo?.teamName}<br />
                            </span>
                          )}
                        </div>
                        <div className="absolute bottom-0 right-0 flex space-x-1 z-10 pointer-events-auto">
                          <button
                            type="button"
                            onClick={handleAvatarUploadClick}
                            className="p-1 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-60 cursor-pointer"
                            disabled={uploadingAvatar}
                            title="Change profile photo"
                          >
                            <Camera className="h-3 w-3" />
                          </button>
                          {isEditing && (
                            <button type="button" className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors" title="Remove photo">
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        {/* Hidden file input for avatar selection */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAvatarFileChange}
                        />
                      </div>

                      {isAvatarPreviewOpen && (
                        <div
                          role="dialog"
                          aria-modal="true"
                          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
                          onClick={() => setIsAvatarPreviewOpen(false)}
                        >
                          <div
                            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-[90%] p-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex justify-between items-center mb-3">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile Photo</h3>
                              <button
                                onClick={() => setIsAvatarPreviewOpen(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                aria-label="Close"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="w-full flex items-center justify-center">
                              {userProfile.personalInfo?.avatar ? (
                                <img
                                  src={userProfile.personalInfo.avatar}
                                  alt="Profile Large"
                                  className="max-h-[70vh] rounded-md object-contain"
                                />
                              ) : (
                                <div className="text-gray-500 dark:text-gray-400">No image available</div>
                              )}
                            </div>
                            <div className="mt-4 flex justify-end gap-3">
                              <button
                                onClick={() => setIsAvatarPreviewOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
                              >
                                Close
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const userId = Number(sessionStorage.getItem('userId'));
                                    if (!userId) throw new Error('User ID not found');
                                    await apiDelete(`/gateway/file-service/jwt/file/profile/${userId}`);
                                    setProfileData(prev => {
                                      if (!prev?.userProfile) return prev;
                                      return {
                                        ...prev,
                                        userProfile: {
                                          ...prev.userProfile,
                                          personalInfo: {
                                            ...prev.userProfile.personalInfo,
                                            avatar: undefined
                                          }
                                        }
                                      };
                                    });
                                    setIsAvatarPreviewOpen(false);
                                    //console.log('Profile photo removed');
                                  } catch (e) {
                                    console.error('Remove profile photo failed', e);
                                  }
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                              >
                                Remove photo
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {isEditing ? `${formData.firstName} ${formData.lastName}` : `${userProfile.personalInfo?.firstName || ''} ${userProfile.personalInfo?.lastName || ''}`}
                        </h3>
                        {isEditing && (
                          <div className="mt-2 flex space-x-2">
                            <button
                              onClick={handleAvatarUploadClick}
                              disabled={uploadingAvatar}
                              className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-60"
                            >
                              <Upload className="h-3 w-3" />
                              <span>{uploadingAvatar ? 'Uploading...' : 'Upload'}</span>
                            </button>
                            <button className="flex items-center space-x-1 px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                              <X className="h-3 w-3" />
                              <span>Remove</span>
                            </button>
                          </div>
                        )}
                        {avatarError && (
                          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{avatarError}</p>
                        )}
                      </div>
                    </div>

                    {/* Personal Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={formData.firstName}
                          disabled={!isEditing}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          disabled={!isEditing}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          disabled={!isEditing}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          disabled={!isEditing}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={formData.dateOfBirth}
                          disabled={!isEditing}
                          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Bio
                        </label>
                        <textarea
                          value={formData.bio}
                          disabled={!isEditing}
                          onChange={(e) => handleInputChange('bio', e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Tell us about yourself..."
                        />
                      </div>
                    </div>
                  </div>
              </div>

              {/* Notifications Tab - Hidden */}
              {false && activeTab === 'notifications' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>{isEditing ? 'Cancel' : 'Edit Settings'}</span>
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Recent Notifications List (from Profile) */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Notifications</h3>
                      <div className="space-y-3">
                        {notifications.map((notification) => (
                          <div key={notification.id} className={`p-4 rounded-lg border ${
                            notification.read 
                              ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
                              : 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700'
                          }`}>
                            <p className="text-sm text-gray-900 dark:text-gray-100">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {new Date(notification.timestamp).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notification Settings (from UserProfileManagement) */}
                    {userProfile.notificationSettings && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Notification Settings</h3>
                        
                        {/* Email Notifications */}
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Email Notifications</h4>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userProfile.notificationSettings.email?.enabled || false}
                                disabled={!isEditing}
                                onChange={() => setHasChanges(true)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">Ticket Updates</span>
                              <input
                                type="checkbox"
                                checked={userProfile.notificationSettings.email?.ticketUpdates || false}
                                disabled={!isEditing}
                                onChange={() => setHasChanges(true)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">Weekly Digest</span>
                              <input
                                type="checkbox"
                                checked={userProfile.notificationSettings.email?.weeklyDigest || false}
                                disabled={!isEditing}
                                onChange={() => setHasChanges(true)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 dark:text-gray-300">System Announcements</span>
                              <input
                                type="checkbox"
                                checked={userProfile.notificationSettings.email?.systemAlerts || false}
                                disabled={!isEditing}
                                onChange={() => setHasChanges(true)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </div>
                          </div>
                        </div>

                        {/* SMS Notifications */}
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">SMS Notifications</h4>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userProfile.notificationSettings.sms?.enabled || false}
                                disabled={!isEditing}
                                onChange={() => setHasChanges(true)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Phone Number
                            </label>
                            <input
                              type="tel"
                              value={userProfile.notificationSettings.sms?.phoneNumber || ''}
                              disabled={!isEditing}
                              onChange={() => setHasChanges(true)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Preferences Tab - Hidden */}
              {false && activeTab === 'preferences' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Preferences</h2>
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>{isEditing ? 'Cancel' : 'Edit'}</span>
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Language
                        </label>
                        <select
                          value={mergedPreferences.language || 'en'}
                          disabled={!isEditing}
                          onChange={() => setHasChanges(true)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                        >
                          {availableLanguages?.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name} ({lang.nativeName})
                            </option>
                          )) || <option value="en">English</option>}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Timezone
                        </label>
                        <select
                          value={mergedPreferences.timezone || 'UTC'}
                          disabled={!isEditing}
                          onChange={() => setHasChanges(true)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                        >
                          {timeZones?.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label} ({tz.offset})
                            </option>
                          )) || <option value="UTC">UTC</option>}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Theme
                        </label>
                        <select
                          value={mergedPreferences.theme || 'auto'}
                          disabled={!isEditing}
                          onChange={() => setHasChanges(true)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                        >
                          <option value="light">Light</option>
                          <option value="dark">Dark</option>
                          <option value="auto">Auto</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Date Format
                        </label>
                        <select
                          value={mergedPreferences.dateFormat || 'MM/DD/YYYY'}
                          disabled={!isEditing}
                          onChange={() => setHasChanges(true)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                        >
                          {dateFormats?.map((format) => (
                            <option key={format.value} value={format.value}>
                              {format.label} ({format.example})
                            </option>
                          )) || <option value="MM/DD/YYYY">MM/DD/YYYY</option>}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity Tab - Hidden */}
              {false && activeTab === 'activity' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Profile History</h2>
                  </div>

                  <div className="space-y-4">
                    {userProfile.profileHistory?.map((change) => (
                      <div key={change.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Edit3 className="h-4 w-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {change.field.charAt(0).toUpperCase() + change.field.slice(1)} Updated
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              <span className="font-medium">From:</span> {change.oldValue} → <span className="font-medium">To:</span> {change.newValue}
                            </div>
                            {change.reason && (
                              <p className="text-sm text-gray-500 dark:text-gray-500">
                                <span className="font-medium">Reason:</span> {change.reason}
                              </p>
                            )}
                          </div>
                          <div className="text-right text-sm text-gray-500 dark:text-gray-500">
                            <p>{new Date(change.changedAt).toLocaleDateString()}</p>
                            <p>{new Date(change.changedAt).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      </div>
                    )) || (
                      <div className="text-center py-8">
                        <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">No activity history yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
        </div>
      </div>
      {changePasswordModal}
    </div>
  );
};

export default UnifiedProfile;

